import { createIsEnumOf, type ReadonlyDate } from '../../utils'

import type { Loadable } from '../Loadable'

type CybusCatalogService = Readonly<{
    /**
     * The version available in the catalog
     */
    version: string

    /**
     * Last time the service was updated in the catalog
     */
    updatedAt: ReadonlyDate

    /**
     * The commissioning file made available by the catalog
     */
    commissioningFile: string

    /**
     * The directory configuration attached to that service
     */
    directory: string

    /**
     * The filename configuration attached to that service
     */
    filename: string

    /**
     * If the service is available on the license
     */
    available: boolean

    category: CybusCatalogApplicationCategory

    name: string

    icon: string | null

    /**
     * Who provided the service
     */
    provider: string | null

    /**
     * Whether the service is also usable as a template
     */
    template: boolean

    description: string

    /**
     * Url of the service
     */
    homepage: string
}>

/**
 * This is the information necessary in order to fetch further service information
 */
export type CybusCatalogServiceConfiguration = Pick<CybusCatalogService, 'filename' | 'directory'>

/**
 * This is the information related to a version of a services catalog maintained service
 */
export type CybusCatalogServiceVersionData = Pick<CybusCatalogService, 'version' | 'updatedAt' | 'commissioningFile'>

export enum CybusCatalogApplicationCategory {
    APPLICATIONS = 'APPLICATIONS',
    NORTHBOUND = 'NORTHBOUND',
    SOUTHBOUND = 'SOUTHBOUND',
}

export const isCybusCatalogApplicationCategory = createIsEnumOf(CybusCatalogApplicationCategory)

export type CybusCatalogApplication = Pick<
    CybusCatalogService,
    'version' | 'updatedAt' | 'directory' | 'filename' | 'available' | 'category' | 'name' | 'icon' | 'provider' | 'template' | 'description' | 'homepage'
>

export type CybusServiceCatalog = Readonly<{
    applications: Loadable<readonly CybusCatalogApplication[]>
    search: string
    filtered: readonly CybusCatalogApplication[]
    template: CybusCatalogApplication | CybusCatalogApplicationCategory | null
    application: CybusCatalogApplication | null
}>
